/**
  * API client for Instagram Commentor backend
  */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Auth token provider type
type AuthTokenProvider = () => Promise<string | null>;

export interface Session {
    id: string;
    status: string;
    accountUrl: string;
    hashtags?: string[];  // Optional hashtags to explore
    vncUrl?: string;
    vmInstanceName?: string;
    progress?: string;  // Detailed progress message from VM
    error?: string;
    createdAt?: string;
    updatedAt?: string;
}

export type CommentStatus = 'underReview' | 'accepted' | 'declined';

export interface Comment {
    id: string;
    postUrl: string;
    username: string;
    content: string;
    imageUrl?: string;
    rationale?: string;
    caption?: string;
    status: CommentStatus;
    selected: boolean;
    declined?: boolean;  // True if user explicitly declined this comment
    posted: boolean;
    postedAt?: string;
    createdAt?: string;
}

export interface UpdateCommentsResponse {
    status: string;  // "updated" or "completed"
    count: number;
}

export interface CommentsIncrementalResponse {
    comments: Comment[];
    totalCount: number;
    hasMore: boolean;
}

export interface CreateSessionResponse {
    id: string;  // Backend returns 'id' not 'sessionId'
    status: string;
}

export interface AccountInfo {
    accountId: string;
    accountUrl: string;
    sessionCount: number;
    lastActivity?: string;
}

export interface PendingComment {
    id: string;
    sessionId: string;
    postUrl: string;
    content: string;
    username: string;
    queuedAt: string;
    status: string;
}

export interface PostingQueuePreview {
    accountId: string;
    totalPending: number;
    comments: PendingComment[];
}

export interface StartPostingQueueResponse {
    queueId: string;
    status: string;
    vncUrl?: string;
    vmInstanceName?: string;
    pendingCount: number;
}

export interface PostingQueueStatus {
    accountId: string;
    status: string;
    pendingCount: number;
    vmStatus?: string;  // "RUNNING", "SUSPENDED", "TERMINATED", etc.
    vmInstanceName?: string;
    vncUrl?: string;
    lastPostedAt?: string;
    isPostingQueueRunning?: boolean;  // True if background monitoring loop is active
    needsLoginSince?: string;  // ISO8601 timestamp when login was first needed
}

export interface FrontendConfig {
    isAutoPostingFeatureActivated: boolean;
    pollEverySecondsForPostingQueueOnSidePanel: number;
}

export interface SyncResult {
    status: string;
    accountId: string;
    syncedCount: number;
    skippedCount: number;
}

// User context types for /users/me/context endpoint
export interface SessionCommentSummary {
    id: string;
    postUrl: string;
    username: string;
    content: string;
    imageUrl?: string;
    rationale?: string;
    caption?: string;
    selected: boolean;
    declined: boolean;
    posted: boolean;
    createdAt?: string;
}

export interface DiscoveredUserSummary {
    id: string;
    username: string;
    userType: string;
    followers?: number;
    reason?: string;
    latestPost?: Record<string, unknown>;
    discoveredAt?: string;
}

export interface PostedCommentSummary {
    id: string;
    sessionId?: string;
    postUrl: string;
    content: string;
    username?: string;
    postedAt?: string;
}

export interface SessionWithDetails {
    id: string;
    status?: string;
    createdAt?: string;
    progress?: string;
    comments: SessionCommentSummary[];
    commentCount: number;
    discoveredUsers: DiscoveredUserSummary[];
}

export interface PendingCommentSummary {
    id: string;
    content: string;
    username?: string;
    postUrl: string;
    queuedAt?: string;
}

export interface AccountWithFullContext {
    accountId: string;
    accountUrl: string;
    sessionCount: number;
    lastActivity?: string;
    isOwner: boolean;
    recentSessions: SessionWithDetails[];
    pendingComments: PendingCommentSummary[];
    pendingCount: number;
    postedComments: PostedCommentSummary[];
    postedUrls: string[];
}

export interface UserInfo {
    firebaseUid: string;
    email?: string;
    displayName?: string;
    photoUrl?: string;
    isAnonymous?: boolean;  // Kept for backward compat, always false for new users
    creditBalance: number;
}

export interface UserContextResponse {
    user?: UserInfo;
    triggeredAccounts: AccountWithFullContext[];
    owningAccounts: AccountInfo[];
}

export interface GetUserContextOptions {
    includeSessions?: boolean;
    sessionsLimit?: number;
    includePendingComments?: boolean;
    pendingCommentsLimit?: number;
    includeSessionComments?: boolean;
    sessionCommentsLimit?: number;
    includeDiscoveredUsers?: boolean;
    discoveredUsersLimit?: number;
    includePostedComments?: boolean;
    postedCommentsLimit?: number;
    includePostedUrls?: boolean;
}

class ApiClient {
    private baseUrl: string;
    private authTokenProvider: AuthTokenProvider | null = null;

    constructor(baseUrl: string = API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    /**
      * Set the auth token provider function.
      * This should be called once when the app initializes with the AuthContext.
      */
    setAuthProvider(provider: AuthTokenProvider): void {
        this.authTokenProvider = provider;
    }

    // Default timeout: 2 minutes (120 seconds) for large comment fetches
    private static readonly DEFAULT_TIMEOUT_MS = 120000;

    private async fetch<T>(
        endpoint: string,
        options: RequestInit = {},
        timeoutMs: number = ApiClient.DEFAULT_TIMEOUT_MS
    ): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;

        // Get auth token if provider is set
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (this.authTokenProvider) {
            const token = await this.authTokenProvider();
            if (token) {
                (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            }
        }

        // Set up timeout with AbortController
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        let response: Response;
        try {
            response = await fetch(url, {
                ...options,
                headers,
                signal: controller.signal,
            });
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === 'AbortError') {
                throw new ApiError(`Request timed out after ${timeoutMs / 1000} seconds`, 'timeout', 0);
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));

            // Handle specific error codes
            if (response.status === 401) {
                throw new ApiError('Authentication required', 'auth_required', 401);
            }
            if (response.status === 402) {
                throw new ApiError('Subscription required', 'subscription_required', 402);
            }
            if (response.status === 403) {
                const detail = error.detail || '';
                if (detail.includes('account_owned_by_other_user') || detail.includes('owned by another')) {
                    throw new ApiError(error.detail || 'Account owned by another user', 'account_owned', 403);
                }
                throw new ApiError(error.detail || 'Access denied', 'forbidden', 403);
            }

            throw new ApiError(error.detail || error.error || `HTTP ${response.status}`, 'unknown', response.status);
        }

        return response.json();
    }

    /**
      * List all instagram accounts that have sessions (for sidebar) once triggered by the user
      */
    async listTriggeredInstagramAccounts(): Promise<AccountInfo[]> {
        return this.fetch<AccountInfo[]>('/sessions/accounts');
    }

    /**
      * List instagram accounts owned by the current user (isOwner=true)
      * Ownership is established when a user successfully posts via VM.
      */
    async listOwningInstagramAccounts(): Promise<AccountInfo[]> {
        return this.fetch<AccountInfo[]>('/sessions/accounts/owning');
    }

    /**
      * List all sessions for a specific account
      */
    async listSessionsForAccount(accountUrl: string): Promise<Session[]> {
        const encodedUrl = encodeURIComponent(accountUrl);
        return this.fetch<Session[]>(`/sessions?account_url=${encodedUrl}`);
    }

    /**
      * List all sessions for the current user
      */
    async listSessions(): Promise<Session[]> {
        return this.fetch<Session[]>('/sessions');
    }

    /**
      * Create a new session
      * @param accountUrl - Instagram account URL to generate comments for
      * @param hashtags - Optional hashtags to explore
      * @param hintIsOwner - Hint from cached context - whether current user owns this account (optimizes Firestore reads)
      * @param hintHasSessions - Hint from cached context - whether account has existing sessions (optimizes Firestore reads)
      */
    async createSession(
        accountUrl: string,
        hashtags?: string[],
        hintIsOwner?: boolean,
        hintHasSessions?: boolean
    ): Promise<CreateSessionResponse> {
        return this.fetch<CreateSessionResponse>('/sessions', {
            method: 'POST',
            body: JSON.stringify({
                accountUrl,
                hashtags,
                hint_is_owner: hintIsOwner,
                hint_has_sessions: hintHasSessions,
            }),
        });
    }

    /**
      * Get session details
      */
    async getSession(sessionId: string): Promise<Session> {
        return this.fetch<Session>(`/sessions/${sessionId}`);
    }

    /**
      * Get comments for a session
      */
    async getComments(sessionId: string): Promise<Comment[]> {
        return this.fetch<Comment[]>(`/sessions/${sessionId}/comments`);
    }

    /**
      * Get comments incrementally for a session.
      * Enables users to start reviewing comments as they are generated.
      * @param sessionId - Session ID
      * @param since - ISO timestamp - only return comments after this time
      * @param limit - Max number of comments to return (default 50)
      */
    async getCommentsIncremental(
        sessionId: string,
        since?: string,
        limit: number = 50
    ): Promise<CommentsIncrementalResponse> {
        let path = `/sessions/${sessionId}/comments/incremental?limit=${limit}`;
        if (since) {
            path += `&since=${encodeURIComponent(since)}`;
        }
        return this.fetch<CommentsIncrementalResponse>(path);
    }

    /**
      * Update comment selections
      * Returns status ("updated" or "completed") and count of updated comments
      */
    async updateComments(
        sessionId: string,
        comments: Array<{ id: string; selected?: boolean; declined?: boolean; content?: string }>
    ): Promise<UpdateCommentsResponse> {
        return this.fetch<UpdateCommentsResponse>(`/sessions/${sessionId}/comments`, {
            method: 'PUT',
            body: JSON.stringify({ comments }),
        });
    }

    /**
      * Trigger posting of selected comments
      */
    async postComments(sessionId: string): Promise<{ status: string; commentCount: number }> {
        return this.fetch(`/sessions/${sessionId}/post`, {
            method: 'POST',
        });
    }

    /**
      * Delete a session
      */
    async deleteSession(sessionId: string): Promise<void> {
        await this.fetch(`/sessions/${sessionId}`, {
            method: 'DELETE',
        });
    }

    /**
      * Get posting queue preview for sidebar (first N pending comments)
      */
    async getPostingQueuePreview(accountId: string, limit: number = 3): Promise<PostingQueuePreview> {
        return this.fetch<PostingQueuePreview>(`/posting-queue/${accountId}/preview?limit=${limit}`);
    }

    /**
      * Get a single queue comment's details
      */
    async getQueueComment(accountId: string, commentId: string): Promise<PendingComment> {
        return this.fetch<PendingComment>(`/posting-queue/${accountId}/comments/${commentId}`);
    }

    /**
      * Update a queue comment's content
      */
    async updateQueueComment(accountId: string, commentId: string, content: string): Promise<PendingComment> {
        return this.fetch<PendingComment>(`/posting-queue/${accountId}/comments/${commentId}`, {
            method: 'PUT',
            body: JSON.stringify({ content }),
        });
    }

    /**
      * Start posting queue for an account (launch or resume posting VM)
      */
    async startPostingQueue(accountUrl: string): Promise<StartPostingQueueResponse> {
        return this.fetch<StartPostingQueueResponse>('/posting-queue', {
            method: 'POST',
            body: JSON.stringify({ accountUrl }),
        });
    }

    /**
      * Get posting queue status for an account (includes VM status)
      * @param accountId - The account ID
      * @param sync - If true (default), sync selected comments to queue first. Use false for polling to improve performance.
      */
    async getPostingQueueStatus(accountId: string, sync: boolean = true): Promise<PostingQueueStatus> {
        return this.fetch<PostingQueueStatus>(`/posting-queue/${accountId}?sync=${sync}`);
    }

    /**
      * Stop posting queue for an account (suspend VM)
      */
    async stopPostingQueue(accountId: string): Promise<{ status: string; message: string }> {
        return this.fetch<{ status: string; message: string }>(`/posting-queue/${accountId}/stop`, {
            method: 'POST',
        });
    }

    /**
      * Get frontend configuration from backend
      */
    async getFrontendConfig(): Promise<FrontendConfig> {
        return this.fetch<FrontendConfig>('/config/frontend');
    }

    /**
      * Mark a queue comment as posted (move from postingQueue to postedComments)
      */
    async markCommentAsPosted(accountId: string, commentId: string): Promise<{ status: string; accountId: string; commentId: string }> {
        return this.fetch<{ status: string; accountId: string; commentId: string }>(`/posting-queue/${accountId}/comments/${commentId}/mark-posted`, {
            method: 'POST',
        });
    }

    /**
      * Sync selected comments to posting queue.
      *
      * Ensures comments marked as selected=true across all sessions
      * are added to the posting queue (if not already queued or posted).
      */
    async syncPostingQueue(accountId: string): Promise<SyncResult> {
        return this.fetch<SyncResult>(`/posting-queue/${accountId}/sync`, {
            method: 'POST',
        });
    }

    /**
      * Delete the current user's account permanently.
      * This deletes all user data including owned Instagram accounts, sessions, comments, etc.
      * This action cannot be undone.
      * @returns Promise with deletion stats
      */
    async deleteAccount(): Promise<{ status: string; accounts_deleted: number; sessions_deleted: number }> {
        return this.fetch('/auth/account', {
            method: 'DELETE',
        });
    }

    /**
      * Get consolidated user context in a single API call.
      * Returns user profile, triggered accounts with sessions, and optional nested data.
      */
    async getUserContext(options: GetUserContextOptions = {}): Promise<UserContextResponse> {
        const params = new URLSearchParams();

        if (options.includeSessions !== undefined) {
            params.append('include_sessions', String(options.includeSessions));
        }
        if (options.sessionsLimit !== undefined) {
            params.append('sessions_limit', String(options.sessionsLimit));
        }
        if (options.includePendingComments !== undefined) {
            params.append('include_pending_comments', String(options.includePendingComments));
        }
        if (options.pendingCommentsLimit !== undefined) {
            params.append('pending_comments_limit', String(options.pendingCommentsLimit));
        }
        if (options.includeSessionComments !== undefined) {
            params.append('include_session_comments', String(options.includeSessionComments));
        }
        if (options.sessionCommentsLimit !== undefined) {
            params.append('session_comments_limit', String(options.sessionCommentsLimit));
        }
        if (options.includeDiscoveredUsers !== undefined) {
            params.append('include_discovered_users', String(options.includeDiscoveredUsers));
        }
        if (options.discoveredUsersLimit !== undefined) {
            params.append('discovered_users_limit', String(options.discoveredUsersLimit));
        }
        if (options.includePostedComments !== undefined) {
            params.append('include_posted_comments', String(options.includePostedComments));
        }
        if (options.postedCommentsLimit !== undefined) {
            params.append('posted_comments_limit', String(options.postedCommentsLimit));
        }
        if (options.includePostedUrls !== undefined) {
            params.append('include_posted_urls', String(options.includePostedUrls));
        }

        const queryString = params.toString();
        const endpoint = queryString ? `/users/me/context?${queryString}` : '/users/me/context';
        return this.fetch<UserContextResponse>(endpoint);
    }
}

/**
  * Custom API error class with error code
  */
export class ApiError extends Error {
    code: string;
    status: number;

    constructor(message: string, code: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
    }
}

export const api = new ApiClient();
export default api;
